import {
  isEqual,
  pick,
  cloneDeep,
  uniq,
  clone,
  sortBy,
  difference,
  isEmpty,
  omit,
  uniqBy,
} from 'lodash'
import semverGt from 'semver/functions/gt'
import semverGte from 'semver/functions/gte'
import semverLte from 'semver/functions/lte'

import { isStoragePath } from '../helpers/file'
import { emptyFamilyTree, uiState } from '../store/initialState'

import migrationsList from './migrations_list'
import { nextColor, nextDarkColor } from '../store/lineColors'
import { emptyFile, newFileFamilyTrees } from '../store/newFileState'
import { toSemver } from './toSemver'
import { safeParseInt } from '../helpers/safeParseInt'

/**
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').AttributeValue} AttributeValue
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Note} Note
 * @typedef {import('../types/plottr').TextChild} TextChild
 * @typedef {import('../types/plottr').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../types/plottr').FamilyTree} FamilyTree
 * @typedef {import('../types/plottr').ImageRecord} ImageRecord
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../types/plottr').AnySlateNode} AnySlateNode
 * @typedef {import('../types/plottr').InternalSchemalessSlateNode} InternalSchemalessSlateNode
 * @typedef {import('../types/plottr').SchemalessSlateNode} SchemalessSlateNode
 * @typedef {import('../types/plottr').SchemalessBulletedList} SchemalessBulletedList
 */

/**
 * Scan through the file for all known locations of Slate content to
 * run the the provided Slate fixer over.  If the file was not changed
 * as a result of running the fixer, simply produce the file
 * unchanged.
 *
 * @param {function(Slate): Slate} fixSlate
 * @return {function(PlottrFile): PlottrFile}
 */
export const fixSlateEverywhere = (fixSlate) => (file) => {
  const mutatedFile = cloneDeep(file)

  /**
   * @param {'scenes' | 'places' | 'notes'} attributeName
   * @return {function(Place | Card | Note): Void}
   */
  const fixAttributesInPlaceCardOrNote = (attributeName) => (placeCardOrNote) => {
    if ('description' in placeCardOrNote && Array.isArray(placeCardOrNote.description)) {
      // NOTE(ed): we're assuming that the argument might not be valid
      // slate and producing something that may be valid.
      //
      // @ts-ignore
      placeCardOrNote.description = fixSlate(placeCardOrNote.description)
    } else if ('content' in placeCardOrNote && Array.isArray(placeCardOrNote.content)) {
      // NOTE(ed): we're assuming that the argument might not be valid
      // slate and producing something that may be valid.
      //
      // @ts-ignore
      placeCardOrNote.content = fixSlate(placeCardOrNote.content)
    } else if ('notes' in placeCardOrNote && Array.isArray(placeCardOrNote.notes)) {
      // NOTE(ed): we're assuming that the argument might not be valid
      // slate and producing something that may be valid.
      //
      // @ts-ignore
      placeCardOrNote.notes = fixSlate(placeCardOrNote.notes)
    }

    if (Array.isArray(mutatedFile?.customAttributes?.[attributeName])) {
      for (const attribute of mutatedFile.customAttributes[attributeName]) {
        const attributeValue = placeCardOrNote[attribute.name]
        if (Array.isArray(attributeValue)) {
          // NOTE(ed): we're assuming that the argument might not be valid
          // slate and producing something that may be valid.
          //
          // @ts-ignore
          placeCardOrNote[attribute.name] = fixSlate(attributeValue)
        }
      }
    }

    if (Array.isArray(placeCardOrNote.templates)) {
      for (const template of placeCardOrNote.templates) {
        if (Array.isArray(template.attributes)) {
          for (let i = 0; i < template.attributes.length; ++i) {
            const templateAttribute = template.attributes[i]
            if (Array.isArray(templateAttribute.value)) {
              // NOTE(ed): we're assuming that the argument might not be valid
              // slate and producing something that may be valid.
              //
              // @ts-ignore
              template.attributes[i].value = fixSlate(templateAttribute.value)
            }
          }
        }
      }
    }
  }

  if (Array.isArray(mutatedFile.cards)) {
    mutatedFile.cards.forEach(fixAttributesInPlaceCardOrNote('scenes'))
  }
  if (Array.isArray(mutatedFile.places)) {
    mutatedFile.places.forEach(fixAttributesInPlaceCardOrNote('places'))
  }
  if (Array.isArray(mutatedFile.notes)) {
    mutatedFile.notes.forEach(fixAttributesInPlaceCardOrNote('notes'))
  }

  if (Array.isArray(mutatedFile.characters)) {
    for (const character of mutatedFile.characters) {
      if (Array.isArray(character.notes)) {
        // NOTE(ed): we're assuming that the argument might not be valid
        // slate and producing something that may be valid.
        //
        // @ts-ignore
        character.notes = fixSlate(character.notes)
      }

      if (Array.isArray(character.attributes)) {
        for (const attribute of character.attributes) {
          if (
            Array.isArray(attribute.value) &&
            attribute.value.every((x) => typeof x === 'object' && x)
          ) {
            // NOTE(ed): we're assuming that the argument might not be valid
            // slate and producing something that may be valid.
            //
            // @ts-ignore
            attribute.value = fixSlate(attribute.value)
          }
        }
      }

      if (Array.isArray(character.templates)) {
        for (const template of character.templates) {
          if (Array.isArray(template.attributes)) {
            for (let i = 0; i < template.attributes.length; ++i) {
              const templateAttribute = template.attributes[i]
              if (Array.isArray(templateAttribute.value)) {
                // NOTE(ed): we're assuming that the argument might not be valid
                // slate and producing something that may be valid.
                //
                // @ts-ignore
                template.attributes[i].value = fixSlate(templateAttribute.value)
              }
            }
          }
          if (Array.isArray(template.values)) {
            for (let i = 0; i < template.values.length; ++i) {
              const attributeValue = template.values[i]
              if (Array.isArray(attributeValue.value)) {
                // NOTE(ed): we're assuming that the argument might not be valid
                // slate and producing something that may be valid.
                //
                // @ts-ignore
                attributeValue.value = fixSlate(attributeValue.value)
              }
            }
          }
        }
      }
    }
  }

  if (isEqual(mutatedFile, file)) {
    return file
  } else {
    return mutatedFile
  }
}

/**
 * I encountered a file in the wild where there's a link that doesn't
 * have a URL on a link element.  This breaks the schema, let's go
 * ahead and fix that.
 *
 * @param {Slate} slate
 * @return {Slate}
 */
export const defaultMissingURLsInLinksLackingAURL = (slate) => {
  const slateCopy = cloneDeep(slate)

  /**
   * @param {AnySlateNode} node
   */
  function iter(node) {
    if ('text' in node) {
      return
    } else {
      switch (node.type) {
        case 'link': {
          if (!('url' in node) || typeof node.url !== 'string') {
            node.url = ''
          }
          return
        }
        case 'heading-one':
        case 'heading-two':
        case 'image-data':
        case 'image-link': {
          // NOTE(ed): there are no link elements allowed as children of these nodes.
          return
        }
        default: {
          for (const child of node.children) {
            iter(child)
          }
          return
        }
      }
    }
  }

  for (const rootElement of slateCopy) {
    iter(rootElement)
  }

  if (!isEqual(slateCopy, slate)) {
    return slateCopy
  } else {
    return slate
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const defaultMissingURLsInLinksLackingAURLEverywhere = fixSlateEverywhere(
  defaultMissingURLsInLinksLackingAURL
)

/**
 * I encountered a file in the wild where there was a `fontSize` of
 * `null`.  The text should not have had a `fontSize` property at all
 * in this case.
 *
 * @param {Slate} slate
 * @return {Slate}
 */
export const removeNullTextPropertiesInText = (slate) => {
  const slateCopy = cloneDeep(slate)

  /**
   * @param {AnySlateNode} node
   */
  function iter(node) {
    if ('text' in node) {
      const propertiesThatCannotBeNull = [
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'font',
        'fontSize',
      ]
      for (const property of propertiesThatCannotBeNull) {
        if (node[property] === null) {
          delete node[property]
        }
      }
    } else {
      for (const child of node.children) {
        iter(child)
      }
      return
    }
  }

  for (const rootElement of slateCopy) {
    iter(rootElement)
  }

  if (!isEqual(slateCopy, slate)) {
    return slateCopy
  } else {
    return slate
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const removeNullTextPropertiesInTextEverywhere = fixSlateEverywhere(
  removeNullTextPropertiesInText
)

/**
 * Fix the parenting of Slate nodes.
 *
 * NOTE(ed): The majority that I've seen in practice are that a node
 * is parented by the wrong parent.
 *
 * @param {Array<InternalSchemalessSlateNode>} slate
 * @return {Array<InternalSchemalessSlateNode>}
 */
export const fixParentingInSlate = (slate) => {
  const mutatedSlate = cloneDeep(slate)

  /**
   * @param {SchemalessSlateNode | Null} parent
   * @param {number} nodeIndex
   * @param {SchemalessSlateNode} node
   * @return {boolean}
   */
  const iter = (parent, nodeIndex, node) => {
    /**
     * @param {SchemalessSlateNode} child
     * @return {['NONE'] | ['SPLICE'] | ['SPLICE_PEER'] | ['WRAP', 'paragraph' | 'bulleted-list' | 'list-item']}
     */
    const determineAction = (child) => {
      if ('text' in child && typeof child.text === 'string') {
        if ('type' in node && (node.type === 'bulleted-list' || node.type === 'numbered-list')) {
          return ['WRAP', 'list-item']
        } else if (
          'type' in node &&
          node.type !== 'paragraph' &&
          node.type !== 'list-item' &&
          node.type !== 'link' &&
          node.type !== 'heading-one' &&
          node.type !== 'heading-two'
        ) {
          return ['WRAP', 'paragraph']
        } else {
          return ['NONE']
        }
      } else if ('type' in child) {
        if (
          'type' in node &&
          node.type === child.type &&
          node.type !== 'bulleted-list' &&
          node.type !== 'numbered-list'
        ) {
          // NOTE(ed): only bulleted-list and numbered-list may be
          // parents of themselves.  Every other duplicate parent
          // chain should be flattened by splicing the children back
          // into the node.
          return ['SPLICE']
        } else {
          switch (child.type) {
            case 'list-item': {
              if ('type' in node && node.type === 'root') {
                return ['WRAP', 'bulleted-list']
              } else if (
                'type' in node &&
                node.type !== 'bulleted-list' &&
                node.type !== 'numbered-list'
              ) {
                return ['SPLICE_PEER']
              } else {
                return ['NONE']
              }
            }
            case 'link': {
              if (
                'type' in node &&
                (node.type === 'bulleted-list' || node.type === 'numbered-list')
              ) {
                return ['WRAP', 'list-item']
              } else if ('type' in node && node.type !== 'paragraph' && node.type !== 'list-item') {
                return ['WRAP', 'paragraph']
              } else {
                return ['NONE']
              }
            }
            case 'bulleted-list':
            case 'numbered-list': {
              if (
                'type' in node &&
                node.type !== 'bulleted-list' &&
                node.type !== 'numbered-list' &&
                node.type !== 'root'
              ) {
                return ['SPLICE_PEER']
              } else {
                return ['NONE']
              }
            }
            case 'image-data':
            case 'image-link':
            case 'paragraph':
            case 'heading-two':
            case 'heading-one': {
              if ('type' in node && node.type !== 'root' && node.type !== 'block-quote') {
                return ['SPLICE_PEER']
              } else {
                return ['NONE']
              }
            }
            case 'block-quote': {
              if ('type' in node && node.type !== 'root') {
                return ['SPLICE_PEER']
              } else {
                return ['NONE']
              }
            }
            default: {
              // To be safe, let's just keep the child when it doesn't
              // look right.  The Slate Editor Normalizer will step in
              // later and fix it up.
              return ['NONE']
            }
          }
        }
      } else {
        return ['NONE']
      }
    }

    /**
     * @return {boolean}
     */
    const processChildren = () => {
      /**
       * @return {boolean}
       */
      const finish = () => {
        if ('type' in node && node.type === 'root') {
          return processChildren()
        } else {
          return true
        }
      }
      if ('children' in node && Array.isArray(node.children)) {
        if (!('type' in node)) {
          // We're very deliberately changing the type of this node
          // here.  The schema of the RCE is totally off and it
          // doesn't specify any type at this point.
          //
          // @ts-ignore
          node.type = 'paragraph'
          return finish()
        } else {
          for (let i = 0; i < node.children.length; ++i) {
            const child = node.children[i]
            const [action, arg] = determineAction(child)
            if (action === 'SPLICE' && 'children' in child) {
              node.children.splice(i, 1, ...child.children)
              return finish()
            } else if (
              action === 'SPLICE_PEER' &&
              parent &&
              'children' in parent &&
              Array.isArray(parent.children)
            ) {
              const atStart = i === 0
              const atEnd = i === node.children.length - 1
              node.children.splice(i, 1)
              if (atStart) {
                // NOTE(ed): Child is at the start of the children, add
                // it before the node in the parent's children.
                parent.children.splice(nodeIndex, 0, child)
              } else if (atEnd) {
                // NOTE(ed): Child is at the end of the children, add it
                // after the node in the parent's children.
                parent.children.splice(nodeIndex + 1, 0, child)
              } else {
                // NOTE(ed): Child is in the middle of the children,
                // split the node into two, and put the child in between
                // those nodes in the parent's children.
                parent.children.splice(nodeIndex + 1, 0, child)
                const secondHalfOfNode = cloneDeep(node)
                // NOTE(ed): we don't slice from i + 1 because we
                // already spliced an element out (at i) a few lines
                // ago.
                secondHalfOfNode.children = secondHalfOfNode.children.slice(i)
                parent.children.splice(
                  nodeIndex + 2,
                  0,
                  // NOTE(ed): these children must be just as valid as
                  // the children that were already there since they
                  // came from a parent with the same type.
                  //
                  // @ts-ignore
                  secondHalfOfNode
                )
                node.children = node.children.slice(0, i)
              }
              return finish()
            } else if (action === 'WRAP') {
              if ('text' in child) {
                node.children.splice(i, 1, { type: arg, children: [child] })
                return finish()
              } else if ('type' in child) {
                // NOTE(ed): the TS compiler has issues if you don't lay
                // out each wrapping case in its own branch.  I think it
                // might be bundling together the property types into
                // unions and then it runs into a problem when you
                // combine the branches :/
                if (child.type === 'list-item' && arg === 'bulleted-list') {
                  node.children.splice(i, 1, { type: arg, children: [child] })
                  return finish()
                } else if (
                  child.type === 'link' &&
                  (node.type === 'bulleted-list' || node.type === 'numbered-list')
                ) {
                  node.children.splice(i, 1, { type: arg, children: [child] })
                  return finish()
                } else if (child.type === 'link' && node.type !== 'paragraph') {
                  node.children.splice(i, 1, { type: arg, children: [child] })
                  return finish()
                }
              }
            } else {
              const mutatedAChild = iter(node, i, child)
              if (mutatedAChild && node.type === 'root') {
                return processChildren()
              } else if (mutatedAChild) {
                return mutatedAChild
              }
            }
          }
        }
        return false
      } else {
        return false
      }
    }
    return processChildren()
  }

  iter(null, 0, { type: 'root', children: mutatedSlate })

  if (isEqual(mutatedSlate, slate)) {
    return slate
  } else {
    return mutatedSlate
  }
}

/**
 * Scan through the file for all known locations of Slate content to
 * run the Slate parnting fix function over.
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const fixParentingInSlateEverywhere = fixSlateEverywhere(
  // NOTE(ed): This fixer expects something that looks Slate-ish so it
  // can fix it's schema.  We're going to ignore the mismatch since
  // this is about producing valid Plottr files from invalid ones.
  //
  // @ts-ignore
  fixParentingInSlate
)

/**
 * I found a file in the wild that had the `ideaBoard` key in the `ui`
 * set to `null`.  I think this is legacy weird behaviour that we need
 * to fix
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addIdeaBoardToUIIfNull = (file) => {
  if (file.ui.ideaBoard === null) {
    return {
      ...file,
      ui: {
        ...file.ui,
        ideaBoard: uiState.ideaBoard,
      },
    }
  } else {
    return file
  }
}

/**
 * The 2021-07-07 version was problematic because it was created by
 * broken templates.  In that version of the templates we had no
 * 'light' and 'dark' keys on hierarchy configuration levels.
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const handle2021_07_07 = (file) => {
  if (file.file.version !== '2021.7.7') return file

  const newFile = clone(file)

  sortBy(Object.entries(newFile.hierarchyLevels), '0').forEach(([index, _levelConfig]) => {
    // NOTE: this migration fixes a previous version of the file
    // schema where there was one hierarchy config per project.
    //
    // @ts-ignore
    newFile.hierarchyLevels[index].dark = {
      borderColor: nextDarkColor(0),
      textColor: nextDarkColor(0),
    }
    // @ts-ignore
    newFile.hierarchyLevels[index].light = {
      borderColor: nextColor(0),
      textColor: nextColor('default'),
    }
  })

  if (!newFile.file.appliedMigrations) {
    newFile.file.appliedMigrations = []
  }
  newFile.file.appliedMigrations.push('m2021_7_7')

  return newFile
}

/**
 * Some users have cards with no `templates` field.  In 2020.3.4 we
 * add the `templates` field to all cards and give it a default of
 * `[]`.  If any cards lack this field and are later than 2020.3.4,
 * then we go ahead and add the field in.
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const handleMissingTemplatesFieldOnCards = (file) => {
  if (semverGt('2020.3.4', file.file.version)) {
    return file
  }

  // Check, and leave the file completely un changed if there were no
  // problems, because that feels safer.
  const thereAreMissingTemplates =
    file &&
    file.cards &&
    file.cards.some((card) => !card.templates || !Array.isArray(card.templates))

  if (!thereAreMissingTemplates) return file

  const appliedMigrations = file.file.appliedMigrations || []

  return {
    ...clone(file),
    file: {
      ...clone(file.file),
      appliedMigrations: [...appliedMigrations, 'm2020_3_5'],
    },
    cards: file.cards?.map((card) => ({
      ...card,
      templates: card.templates || [],
    })),
  }
}

/**
 * @param {any} x
 * @return {boolean}
 */
const isObject = (x) => x && typeof x === 'object' && x.constructor === Object

/**
 * There was a bug on mobile that saved the title of a scene card to
 * `{ title, description }` rather than `title`.
 * and the description was gone elsewhere besides inside the title
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const handleObjectTitlesOnCards = (file) => {
  const FIX_VERSION = 'm2022_1_11'

  const appliedMigrations =
    (file && file.file && file.file.appliedMigrations && file.file.appliedMigrations) || []

  if (appliedMigrations.indexOf(FIX_VERSION) > -1) {
    return file
  }

  const fixApplies =
    file &&
    file.cards &&
    file.cards.some(
      (card) =>
        typeof card.title !== 'string' &&
        isObject(card.title) &&
        // NOTE: the bug this fixes in a file is that the file did
        // not adhere to the schema(!)
        // @ts-ignore
        typeof card.title.title === 'string' &&
        // @ts-ignore
        Array.isArray(card.title.description)
    )

  if (!fixApplies) {
    return file
  }

  return {
    ...file,
    file: {
      ...file.file,
      appliedMigrations: [...appliedMigrations, FIX_VERSION],
    },
    cards: file.cards.map((card) => {
      if (typeof card.title !== 'string' && isObject(card.title)) {
        // @ts-ignore
        if (typeof card.title.title !== 'string') {
          console.warn(
            `Card: ${card} has an object instead of its title, but that object has no title(!)`
          )
          return card
        }
        // @ts-ignore
        if (!Array.isArray(card.title.description)) {
          console.warn(
            `Card: ${card} has an object instead of its title, but that object has no description(!)`
          )
          return card
        }
        return {
          ...card,
          // @ts-ignore
          title: card.title.title,
          // @ts-ignore
          description: card.title.description,
        }
      }
      return card
    }),
  }
}

/**
 * @type {Array<keyof PlottrFile['ui']>}
 */
// This is definitely safe because uiState is of type PlottrFileUi
// in the initial state file.
//
// @ts-ignore
export const MINIMAL_SET_OF_UI_KEYS = [...Object.keys(uiState)]

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const handleMissingUIState = (file) => {
  const fixApplies =
    !file.ui ||
    MINIMAL_SET_OF_UI_KEYS.some((key) => {
      return typeof file.ui[key] === 'undefined'
    })

  if (!fixApplies) {
    return file
  }

  return MINIMAL_SET_OF_UI_KEYS.reduce((acc, key) => {
    if (acc.ui && typeof acc.ui[key] !== 'undefined') {
      return acc
    }
    return {
      ...acc,
      ui: {
        ...acc.ui,
        [key]: uiState[key],
      },
    }
  }, file)
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addCardNotesIfMissing = (file) => {
  const fixApplies = !file.cards

  if (!fixApplies) {
    return file
  }

  const updatedCards = file.cards.map((c) => {
    if (!c.notes) {
      return {
        ...c,
        notes: [],
      }
    }
    return c
  })

  return {
    ...file,
    cards: updatedCards,
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const insertBreakingVersionsPriorToBreakingVersionChange = (file) => {
  const breakingMigrations = migrationsList.filter((mig) => mig.includes('*'))
  const appliedMigrations = file.file.appliedMigrations || []
  const missingBreakingMigrations = difference(breakingMigrations, appliedMigrations).filter(
    (missingVersion) => {
      // @ts-ignore
      return semverLte(toSemver(missingVersion), toSemver(file.file.version))
    }
  )

  if (missingBreakingMigrations.length > 0) {
    return {
      ...file,
      file: {
        ...file.file,
        appliedMigrations: [...appliedMigrations, ...missingBreakingMigrations],
      },
    }
  }

  return file
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addPinnedPlotlinesIfMissing = (file) => {
  const bookId = file.ui?.currentTimeline
  const totalPinnedLinesFromLines = (file.lines || []).reduce(
    (prevCount, line) => (line.isPinned ? prevCount + 1 : prevCount),
    0
  )
  const hasUITimeline = !isEmpty(file.ui?.timeline)
  const newFile = emptyFile('', '')
  const timeline = hasUITimeline ? file.ui.timeline : newFile.ui.timeline
  const pinnedPlotlines = hasUITimeline ? file.ui.timeline?.pinnedPlotlines : {}
  const pinnedPlotlinesFromCurrentBook = file.ui.timeline?.pinnedPlotlines?.[String(bookId)]

  if (
    (!isEmpty(pinnedPlotlines) && totalPinnedLinesFromLines !== pinnedPlotlinesFromCurrentBook) ||
    typeof pinnedPlotlinesFromCurrentBook !== 'number'
  ) {
    return {
      ...file,
      ui: {
        ...file.ui,
        timeline: {
          ...timeline,
          pinnedPlotlines: {
            [bookId]: totalPinnedLinesFromLines,
          },
        },
      },
    }
  } else {
    return file
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addPlotlinePinnedStateIfMissing = (file) => {
  const allLines = file.lines
  const allLinesHasPinnedState = allLines.every((l) => typeof l.isPinned === 'boolean')

  if (!allLinesHasPinnedState) {
    const fixedLines = allLines.map((l) => {
      return typeof l.isPinned === 'boolean' ? l : { ...l, isPinned: false }
    })
    return {
      ...file,
      lines: fixedLines,
    }
  } else {
    return file
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addHierarchiesIfMissing = (file) => {
  const allIds = file.books?.allIds
  if (
    Array.isArray(allIds) &&
    (typeof file.hierarchyLevels?.series === 'undefined' ||
      allIds.some((id) => typeof file.hierarchyLevels[id] === 'undefined')) &&
    semverGte(file.file.version, '2023.3.29')
  ) {
    const newFile = emptyFile('', '')
    return {
      ...file,
      hierarchyLevels: {
        ...(allIds || []).reduce((acc, next) => {
          return {
            ...acc,
            [next]: newFile.hierarchyLevels['1'],
          }
        }, {}),
        ...file.hierarchyLevels,
        series: newFile.hierarchyLevels.series,
      },
    }
  } else {
    return file
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addUITimelineOrHierarchiesStateIfMissing = (file) => {
  const withHierarchies = addHierarchiesIfMissing(file)
  return addPinnedPlotlinesIfMissing(withHierarchies)
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const removeCharacterAttributesForNonExistingBooks = (file) => {
  const allIds = file.books?.allIds
  if (Array.isArray(allIds)) {
    /**
     * @type {Array<AttributeValue>}
     */
    const attributesOnCharacters = uniq(
      (file.characters || []).flatMap(({ attributes }) => {
        return attributes || []
      })
    )
    /**
     * @param {AttributeValue} attribute
     * @return {Boolean}
     */
    const isAttributeForNonExistingBook = (attribute) => {
      return attribute.bookId !== 'all' && allIds.indexOf(attribute.bookId) === -1
    }
    const brokenCharacterAttributes = attributesOnCharacters.filter(isAttributeForNonExistingBook)
    if (brokenCharacterAttributes.length === 0) {
      return file
    } else {
      const fixedCharacters = (file.characters || []).map((character) => {
        const { attributes } = character
        if (!attributes) {
          return character
        }
        const brokenAttributes = attributes.filter(isAttributeForNonExistingBook)
        if (brokenAttributes.length === 0) {
          return character
        } else {
          return {
            ...character,
            attributes: attributes.filter((attribute) => !isAttributeForNonExistingBook(attribute)),
          }
        }
      })
      return {
        ...file,
        characters: fixedCharacters,
      }
    }
  } else {
    return file
  }
}

/**
 * Mobile might still create legacy character attributes until it's
 * upgraded.  Desktop/web handles legacy attributes fine, but they may
 * overlap with built-in attributes (they do in the templates).
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const migrateLegacyCharacterAttributes = (file) => {
  if (
    !Array.isArray(file?.customAttributes?.characters) ||
    !Array.isArray(file?.characters) ||
    file?.customAttributes?.characters?.length === 0 ||
    file?.characters?.length === 0
  ) {
    return file
  } else {
    const newAttributes = file.attributes?.characters ?? []
    const maxCharacterAttributeId = newAttributes.reduce((maxId, next) => {
      return Math.max(maxId, next.id)
    }, 1)
    const withLegacyAttributes = file.customAttributes.characters.reduce(
      (attributes, nextLegacyAttribute, index) => {
        const alreadyHasAttribute = attributes.some((attribute) => {
          return attribute.name === nextLegacyAttribute.name
        })
        if (alreadyHasAttribute) {
          return attributes
        } else {
          return [
            ...attributes,
            {
              id: maxCharacterAttributeId + index + 1,
              name: nextLegacyAttribute.name,
              type: nextLegacyAttribute.type,
            },
          ]
        }
      },
      newAttributes
    )
    /**
     * @param {Character} character
     * @return {boolean}
     */
    const characterHasLegacyAttributeDefined = (character) => {
      return file.customAttributes?.characters?.some?.((legacyAttribute) => {
        // We're testing for the presence of a legacy attribute.  They
        // break the schema of a Character.
        //
        // @ts-ignore
        return typeof character[legacyAttribute.name] !== 'undefined'
      })
    }
    const aCharacterHasALegacyAttributeDefined = file.characters.some(
      characterHasLegacyAttributeDefined
    )
    const newCharacters = aCharacterHasALegacyAttributeDefined
      ? file.characters.map((character) => {
          const hasALegacyAttributeDefined = characterHasLegacyAttributeDefined(character)
          if (hasALegacyAttributeDefined) {
            /**
             * @type {function(Character, LegacyCustomAttribute): Character}
             */
            const accumulateCharacter = (characterAcc, nextLegacyAttribute) => {
              // We're testing for the presence of a legacy attribute.  They
              // break the schema of a Character.
              //
              // @ts-ignore
              const legacyAttributeValue = character[nextLegacyAttribute.name]
              if (
                typeof legacyAttributeValue !== 'undefined' &&
                (nextLegacyAttribute.name !== 'attributes' ||
                  (nextLegacyAttribute.name === 'attributes' &&
                    (typeof legacyAttributeValue === 'string' ||
                      // This should be a good enough check we're not
                      // dealing with a new character attribute.
                      (Array.isArray(legacyAttributeValue) &&
                        typeof legacyAttributeValue[0]?.id !== 'number'))))
              ) {
                /**
                 * The compiler is confused that we sometimes omit
                 * "attributes" because in older schemas you could use
                 * that name for an attribute.
                 *
                 * @type {Character}
                 */
                // @ts-ignore
                const withoutLegacyAttribute = omit(characterAcc, nextLegacyAttribute.name)
                const newAttributeId = withLegacyAttributes.find((newAttribute) => {
                  return (
                    newAttribute.id > maxCharacterAttributeId &&
                    newAttribute.name === nextLegacyAttribute.name
                  )
                })?.id
                if (typeof newAttributeId === 'number') {
                  return {
                    ...withoutLegacyAttribute,
                    attributes: [
                      ...(characterAcc.attributes ?? []),
                      {
                        id: newAttributeId,
                        bookId: 'all',
                        value: legacyAttributeValue,
                      },
                    ],
                  }
                } else {
                  return withoutLegacyAttribute
                }
              } else {
                return characterAcc
              }
            }
            return file.customAttributes.characters.reduce(accumulateCharacter, character)
          } else {
            return character
          }
        })
      : file.characters
    return {
      ...file,
      characters: newCharacters,
      customAttributes: {
        ...file.customAttributes,
        characters: [],
      },
      attributes: {
        ...(file.attributes ?? {}),
        characters: withLegacyAttributes,
      },
    }
  }
}

export const IMAGE_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAABhGlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9TpX5UHawg4pChOlkQFXHUKhShQqgVWnUwufQLmjQkKS6OgmvBwY/FqoOLs64OroIg+AHi6uKk6CIl/i8ptIj14Lgf7+497t4BQrXINKttHNB020zEomIqvSoGXtGNXnRiAJCZZcxJUhwtx9c9fHy9i/Cs1uf+HD1qxmKATySeZYZpE28QT2/aBud94hDLyyrxOfGYSRckfuS64vEb55zLAs8MmcnEPHGIWMw1sdLELG9qxFPEYVXTKV9Ieaxy3uKsFcusfk/+wmBGX1nmOs1hxLCIJUgQoaCMAoqwEaFVJ8VCgvajLfxDrl8il0KuAhg5FlCCBtn1g//B726t7OSElxSMAu0vjvMxAgR2gVrFcb6PHad2AvifgSu94S9VgZlP0isNLXwE9G0DF9cNTdkDLneAwSdDNmVX8tMUslng/Yy+KQ303wJda15v9X2cPgBJ6ip+AxwcAqM5yl5v8e6O5t7+PVPv7wf7enJ3iw8StgAAAAZiS0dEAP8A/wD/oL2nkwAAAAlwSFlzAAAuIwAALiMBeKU/dgAAAAd0SU1FB+cLDgkYCNOL7FwAAAAZdEVYdENvbW1lbnQAQ3JlYXRlZCB3aXRoIEdJTVBXgQ4XAAAARklEQVRYw+3YIQ4AIAwEwSvh/18GiwZTkllVOTnZSrLSuJHmzeOuZrb1xYKAgICAgICAgICAgICAgICAgICAgIC3VXz539rLOwNPnv65RAAAAABJRU5ErkJggg=='

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const removeDudImages = (file) => {
  /**
   * @param {ImageRecord} image
   * @return {boolean}
   */
  const isBroken = (image) => {
    const isLocalImageThatLacksData =
      (typeof image.path === 'undefined' || (image.path && !isStoragePath(image.path))) &&
      typeof image.data !== 'string'
    const lacksAName = typeof image.name !== 'string'
    const invalidId = typeof image.id !== 'number'
    return isLocalImageThatLacksData || lacksAName || invalidId
  }
  const hasBrokenImage = Object.values(file.images).some(isBroken)
  let newId = Object.values(file.images).length + 1
  /**
   * @param {ImageRecord} inputImage
   * @return {ImageRecord}
   */
  const fixImage = (inputImage) => {
    /**
     * @param {ImageRecord} image
     * @return {ImageRecord}
     */
    const fixMissingData = (image) => {
      const isLocalImageThatLacksData =
        (typeof image.path === 'undefined' || (image.path && !isStoragePath(image.path))) &&
        typeof image.data !== 'string'
      if (isLocalImageThatLacksData) {
        return {
          ...image,
          data: IMAGE_PLACEHOLDER,
        }
      } else {
        return image
      }
    }
    /**
     * @param {ImageRecord} image
     * @return {ImageRecord}
     */
    const fixMissingName = (image) => {
      const lacksAName = typeof image.name !== 'string'
      if (lacksAName) {
        return {
          ...image,
          name: ' ',
        }
      } else {
        return image
      }
    }
    /**
     * @param {ImageRecord} image
     * @return {ImageRecord}
     */
    const fixInvalidId = (image) => {
      const invalidId = typeof image.id !== 'number'
      if (invalidId) {
        return {
          ...image,
          id: newId++,
        }
      } else {
        return image
      }
    }

    return fixMissingData(fixMissingName(fixInvalidId(inputImage)))
  }
  if (hasBrokenImage) {
    return {
      ...file,
      images: Object.entries(file.images).reduce((imagesAcc, nextKeyValue) => {
        const [key, image] = nextKeyValue
        const fixedImage = fixImage(image)
        return {
          ...imagesAcc,
          [fixedImage.id || key]: fixedImage,
        }
      }, {}),
    }
  } else {
    return file
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const removeDuplicateCustomAttributes = (file) => {
  const customAttributes = file.customAttributes

  // guard for test files without custom attributes
  if (isEmpty(customAttributes)) {
    return file
  }

  const cardsCustomAttr = customAttributes['scenes'] ?? []
  const notesCustomAttr = customAttributes['notes'] ?? []
  const placesCustomAttr = customAttributes['places'] ?? []

  const uniqCardAttr = uniqBy(cardsCustomAttr, 'name')
  const uniqNoteAttr = uniqBy(notesCustomAttr, 'name')
  const uniqPlaceAttr = uniqBy(placesCustomAttr, 'name')

  const hasDuplicateCardAttrs = uniqCardAttr.length != cardsCustomAttr.length
  const hasDuplicateNoteAttrs = uniqNoteAttr.length != notesCustomAttr.length
  const hasDuplicatePlaceAttrs = uniqPlaceAttr.length != placesCustomAttr.length

  if (!hasDuplicateCardAttrs && !hasDuplicateNoteAttrs && !hasDuplicatePlaceAttrs) {
    return file
  }

  return {
    ...file,
    customAttributes: {
      ...file.customAttributes,
      scenes: hasDuplicateCardAttrs ? uniqCardAttr : cardsCustomAttr,
      notes: hasDuplicateNoteAttrs ? uniqNoteAttr : notesCustomAttr,
      places: hasDuplicatePlaceAttrs ? uniqPlaceAttr : placesCustomAttr,
    },
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const addFamilyTreeIfMissing = (file) => {
  if (!file.familyTrees) {
    return {
      ...file,
      familyTrees: cloneDeep(newFileFamilyTrees),
    }
  } else {
    return file
  }
}

/**
 * Produce true if x is null or a string.
 *
 * @param {any} x
 */
const isNullOrString = (x) => {
  return x === null || typeof x === 'string'
}

/**
 * Produce true if x is null or an object.
 *
 * @param {any} x
 */
const isNullOrObject = (x) => {
  return x === null || typeof x === 'object'
}

/**
 * Produce true if the given object matches the family tree schema.
 *
 * @param {Object} maybeFamilyTree
 * @return {boolean}
 */
const matchesFamilyTreeSchema = (maybeFamilyTree) => {
  return (
    'id' in maybeFamilyTree &&
    typeof maybeFamilyTree.id === 'number' &&
    'name' in maybeFamilyTree &&
    typeof maybeFamilyTree.name === 'string' &&
    'stage' in maybeFamilyTree &&
    typeof maybeFamilyTree.stage === 'object' &&
    'members' in maybeFamilyTree &&
    typeof maybeFamilyTree.members === 'object' &&
    'backgroundImageId' in maybeFamilyTree &&
    isNullOrString(maybeFamilyTree.backgroundImageId) &&
    'backgroundColor' in maybeFamilyTree &&
    isNullOrString(maybeFamilyTree.backgroundColor) &&
    'backgroundImageTranslation' in maybeFamilyTree &&
    isNullOrObject(maybeFamilyTree.backgroundImageTranslation) &&
    'relationshipConfiguration' in maybeFamilyTree &&
    typeof maybeFamilyTree.relationshipConfiguration === 'object'
  )
}

/**
 * Produce true if any of the provided family tree definitions is
 * invalid.
 *
 * @param {Record<String, FamilyTree>} familyTrees
 * @return {boolean}
 */
const anyTreeIsInvalid = (familyTrees) => {
  return Object.entries(familyTrees).some(([key, maybeFamilyTree]) => {
    return safeParseInt(key).toString() !== key || !matchesFamilyTreeSchema(maybeFamilyTree)
  })
}

/**
 * Produce true if the provided object matches a partial schema of a
 * family tree.
 *
 * @param {any} x
 * @return {boolean}
 */
const isPartialFamilyTree = (x) => {
  return 'id' in x && typeof x.id === 'number' && 'members' in x && typeof x.members === 'object'
}

/**
 * Produce a valid family tree for each entry in the given record of
 * family trees.
 *
 * @param {Record<String, FamilyTree>} familyTrees
 * @return {Record<String, FamilyTree>}
 */
const fixFamilyTrees = (familyTrees) => {
  const familyTreeKeys = [...Object.keys(emptyFamilyTree)]
  return Object.entries(familyTrees).reduce((acc, [key, next]) => {
    if (matchesFamilyTreeSchema(next) || isPartialFamilyTree(next)) {
      if (Object.keys(next).length === 8) {
        return {
          ...acc,
          [key]: next,
        }
      } else {
        return {
          ...acc,
          [key]: {
            ...cloneDeep(emptyFamilyTree),
            ...pick(next, ...familyTreeKeys),
          },
        }
      }
    } else {
      return acc
    }
  }, {})
}

/**
 * In versions of Plottr with Family Tree bundled and hidden, an undo
 * might place the "notes" data into the "familyTree" key.  (Doh!)  As
 * a result, there may be files with invalid family trees without the
 * user ever having used family tree.
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const fixInvalidFamilyTrees = (file) => {
  if (Array.isArray(file.familyTrees)) {
    return {
      ...file,
      familyTrees: newFileFamilyTrees,
    }
  } else if (file.familyTrees && anyTreeIsInvalid(file.familyTrees)) {
    return {
      ...file,
      familyTrees: fixFamilyTrees(file.familyTrees),
    }
  } else {
    return file
  }
}

/**
 * Produce true if any of the relationships in members of the family
 * tree bear names that don't exist in the relationship configuration.
 *
 * @param {FamilyTree} familyTree
 * @return {boolean}
 */
export const familyTreeHasInvalidRelationship = (familyTree) => {
  const config = familyTree.relationshipConfiguration

  return Object.values(familyTree.members).some(({ relationships }) => {
    return relationships.some(({ relationshipName }) => {
      return !config[relationshipName]
    })
  })
}

/**
 * @typedef Coord
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {'DOWN' | 'UP' | 'LEFT' | 'RIGHT'} Direction
 */

/**
 * Produce a suitable name for the orientation of destCoord with
 * respect to sourceCoord.
 *
 * (Copied from family tree code for creating a new relationship.)
 *
 * @param {Coord} sourceCoord
 * @param {Coord} destCoord
 * @return {Direction}
 */
function orientationOfPoints(sourceCoord, destCoord) {
  if (destCoord.y > sourceCoord.y) {
    return 'DOWN'
  } else if (destCoord.y < sourceCoord.y) {
    return 'UP'
  } else if (destCoord.x < sourceCoord.x) {
    return 'LEFT'
  } else {
    return 'RIGHT'
  }
}

/**
 * Produce a valid family tree by defaulting any unkown relationships
 * to one which best suites the spacial arrangement between characters
 * who have invalid relationships between them.  An invalid
 * relationship is defined here as one which is named by a
 * relationship that doesn't exist in the relationship configuration.
 *
 * @param {FamilyTree} familyTree
 * @return {FamilyTree}
 */
export const defaultInvalidRelationshipNames = (familyTree) => {
  const config = familyTree.relationshipConfiguration

  const defaultUpRelationship =
    Object.values(config).find(({ type }) => {
      return type === 'parent'
    }) ?? Object.values(config)[0]
  const defaultDownRelationship =
    Object.values(config).find(({ type }) => {
      return type === 'child'
    }) ?? Object.values(config)[0]
  const defaultSideBySideRelationship =
    Object.values(config).find(({ type }) => {
      return type === 'sibling'
    }) ?? Object.values(config)[0]

  return {
    ...familyTree,
    members: Object.entries(familyTree.members).reduce((members, nextMember) => {
      const [memberId, member] = nextMember

      return {
        ...members,
        [memberId]: {
          ...member,
          relationships: member.relationships.map((relationship) => {
            const isInvalid = !config[relationship.relationshipName]
            if (isInvalid) {
              const orientation = orientationOfPoints(
                member,
                familyTree.members[relationship.characterId]
              )
              if (orientation === 'UP') {
                return {
                  ...relationship,
                  relationshipName: defaultUpRelationship.name,
                }
              } else if (orientation === 'DOWN') {
                return {
                  ...relationship,
                  relationshipName: defaultDownRelationship.name,
                }
              } else {
                return {
                  ...relationship,
                  relationshipName: defaultSideBySideRelationship.name,
                }
              }
            } else {
              return relationship
            }
          }),
        },
      }
    }, {}),
  }
}

/**
 * Fix relationship lines broken by changing relationship names in
 * v2025.8.28.  The relationship name was updated in the relationship
 * configuration but not on the relationships of the character.
 *
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
export const fixInvalidRelationshipNames = (file) => {
  const thereIsAnInvalidRelationshipName = Object.values(file.familyTrees).some(
    familyTreeHasInvalidRelationship
  )

  if (thereIsAnInvalidRelationshipName) {
    return {
      ...file,
      familyTrees: Object.entries(file.familyTrees).reduce((familyTrees, nextFamilyTree) => {
        const [familyTreeId, familyTree] = nextFamilyTree
        if (familyTreeHasInvalidRelationship(familyTree)) {
          return {
            ...familyTrees,
            [familyTreeId]: defaultInvalidRelationshipNames(familyTree),
          }
        } else {
          return {
            ...familyTrees,
            [familyTreeId]: familyTree,
          }
        }
      }, {}),
    }
  } else {
    return file
  }
}

/**
 * @param {PlottrFile} file
 * @return {PlottrFile}
 */
const applyAllFixes = (file) =>
  [
    handle2021_07_07,
    handleMissingTemplatesFieldOnCards,
    handleObjectTitlesOnCards,
    handleMissingUIState,
    insertBreakingVersionsPriorToBreakingVersionChange,
    addUITimelineOrHierarchiesStateIfMissing,
    migrateLegacyCharacterAttributes,
    removeCharacterAttributesForNonExistingBooks,
    removeDuplicateCustomAttributes,
    addFamilyTreeIfMissing,
    addCardNotesIfMissing,
    fixInvalidFamilyTrees,
    fixInvalidRelationshipNames,
    addPlotlinePinnedStateIfMissing,
    fixParentingInSlateEverywhere,
    addIdeaBoardToUIIfNull,
    defaultMissingURLsInLinksLackingAURLEverywhere,
    removeNullTextPropertiesInTextEverywhere,
  ].reduce((acc, f) => f(acc), file)

export default applyAllFixes
